import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";

export type ConfirmButtonProps = {
  onConfirm: () => void;
  children: ReactNode;
  confirmLabel?: ReactNode;
  confirmWindowMs?: number;
  style?: CSSProperties;
  confirmStyle?: CSSProperties;
  ariaLabel?: string;
};

export function ConfirmButton({
  onConfirm,
  children,
  confirmLabel = "Confirm",
  confirmWindowMs = 3000,
  style,
  confirmStyle,
  ariaLabel,
}: ConfirmButtonProps) {
  const [armed, setArmed] = useState(false);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!armed) return;
    timerRef.current = window.setTimeout(() => setArmed(false), confirmWindowMs);
    return () => {
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
    };
  }, [armed, confirmWindowMs]);

  return (
    <button
      type="button"
      aria-label={ariaLabel}
      aria-pressed={armed}
      onClick={(e) => {
        e.stopPropagation();
        if (!armed) {
          setArmed(true);
          return;
        }
        setArmed(false);
        onConfirm();
      }}
      style={{
        minHeight: "44px",
        minWidth: "44px",
        cursor: "pointer",
        fontFamily: "inherit",
        transition: "background 0.15s ease, color 0.15s ease",
        ...style,
        ...(armed ? confirmStyle : {}),
      }}
    >
      {armed ? confirmLabel : children}
    </button>
  );
}
